import { type Dayjs } from 'dayjs';
import { type ScheduledMeal, type Site, type SiteMenuOrder } from '../entities';
import { type SiteMenuOrderView } from '../entities/site-menu-order';

import { weekDays } from '../types';

const getAuthorizationHeader = (token: string): Record<string, string> => ({
  authorization: `Bearer ${token}`,
});

const stringToColor = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const c = (hash & 0x00ffffff).toString(16).toUpperCase();
  return '#' + '00000'.substring(0, 6 - c.length) + c;
};

const cleanEmptyProps = (obj: Record<string, any>): Record<string, any> =>
  Object.entries(obj).reduce((a: any, [k, v]) => (v == null ? a : ((a[k] = v), a)), {});

export const randomUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const serviceTypesLabels = {
  C1: 'C1',
  C2: 'C2',
};

const serveTypesLabels = {
  BULK: 'Bulk',
  INDIVIDUAL: 'Individual',
  TRAY: 'Tray',
};
const serveToolsLabels = {
  UTENSIL: 'Utensil',
  LADLE: 'Ladle',
  TONG: 'Tong',
};

const groupBy = <T,>(
  array: T[],
  predicate: (value: T, index: number, array: T[]) => string | number
): Record<string | number, T[]> =>
  array.reduce<Record<string | number, T[]>>((acc, value, index, array) => {
    (acc[predicate(value, index, array)] ||= []).push(value);
    return acc;
  }, {});

const groupByToMap = <T, Q>(
  array: T[],
  predicate: (value: T, index: number, array: T[]) => Q
): Map<Q, T[]> =>
  array.reduce((map, value, index, array) => {
    const key = predicate(value, index, array);
    map.get(key)?.push(value) ?? map.set(key, [value]);
    return map;
  }, new Map<Q, T[]>());

const printFile = (url: string): void => {
  const hiddenFrame = document.createElement('iframe');
  hiddenFrame.onload = (): void => {
    const closePrint = (): void => {
      document.body.removeChild(hiddenFrame);
    };
    hiddenFrame.contentWindow!.onbeforeunload = closePrint;
    hiddenFrame.contentWindow!.onafterprint = closePrint;
    hiddenFrame.contentWindow!.print();
  };
  hiddenFrame.style.display = 'none'; // hide iframe
  hiddenFrame.src = url ?? '';
  document.body.appendChild(hiddenFrame);
};

const downloadFile = (url: string, filename: string): void => {
  const a = document.createElement('a');
  a.setAttribute('download', filename);
  a.setAttribute('href', url);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

const openFileInNewWindow = (url: string): void => {
  window.open(url);
};

export const isUUID = (str: string): boolean => {
  if (str == null) return false;
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

export const htmlUnescape = (str: string): string => {
  const doc = new DOMParser().parseFromString(str, 'text/html');
  return doc.documentElement.textContent ?? str;
};
export const formatSiteDisplayName = (site: any): string => {
  const displaySiteNumber = site.displaySiteNumber ? `(${site.displaySiteNumber})` : '';
  const siteCounty = site.county ? `(${site.county})` : '';
  return `${site.name} ${displaySiteNumber} ${siteCounty}`;
};

export const sortSitesByCountyAndName = (sites: Site[]): Site[] => {
  return sites.toSorted((site1, site2) => {
    const county1: string = site1?.county ?? '';
    const county2: string = site2?.county ?? '';
    const name1: string = site1?.name ?? '';
    const name2: string = site2?.name ?? '';
    const countyComparison = county1.localeCompare(county2);
    if (countyComparison === 0) {
      return name1.localeCompare(name2);
    }
    return countyComparison;
  });
};
export const toSortedSitesByCustomerAndCountyAndName = (sites: Site[]): Site[] => {
  return sites.toSorted((site1, site2) => {
    const customerName1 = site1?.customer?.name ?? '';
    const customerName2 = site2?.customer?.name ?? '';

    const county1 = site1?.county ?? '';
    const county2 = site2?.county ?? '';

    const siteName1 = site1?.name ?? '';
    const siteName2 = site2?.name ?? '';

    const customerComparison = customerName1.localeCompare(customerName2);
    if (customerComparison !== 0) {
      return customerComparison;
    }

    const countyComparison = county1.localeCompare(county2);
    if (countyComparison !== 0) {
      return countyComparison;
    }

    return siteName1.localeCompare(siteName2);
  });
};

export const sortSiteMenuOrdersByCountyAndName = (
  siteMenuOrders: SiteMenuOrder[] | SiteMenuOrderView[]
): SiteMenuOrder[] | SiteMenuOrderView[] => {
  return siteMenuOrders.toSorted((smo1, smo2) => {
    const county1: string = smo1?.site?.county ?? '';
    const county2: string = smo2?.site?.county ?? '';
    const name1: string = smo1?.site?.name ?? '';
    const name2: string = smo2?.site?.name ?? '';
    const countyComparison = county1.localeCompare(county2);
    if (countyComparison === 0) {
      const siteComparison = name1.localeCompare(name2);
      if (siteComparison === 0) {
        return smo1.id?.localeCompare(smo2.id ?? '') ?? 0;
      }
      return siteComparison;
    }
    return countyComparison;
  });
};

export const sortSiteGroupsByCustomerName = <T extends { customer?: { name?: string } | null }>(
  siteGroups: T[][]
): T[][] => {
  return siteGroups.toSorted((group1, group2) => {
    const customerName1 = group1[0]?.customer?.name ?? '';
    const customerName2 = group2[0]?.customer?.name ?? '';
    return customerName1.localeCompare(customerName2);
  });
};

export const sortScheduledMealsByMealCategorySortOrder = (
  scheduledMeals: ScheduledMeal[]
): ScheduledMeal[] => {
  return scheduledMeals.toSorted((meal1, meal2) => {
    const sortOrder1 = meal1?.mealCategory?.sortOrder ?? 0;
    const sortOrder2 = meal2?.mealCategory?.sortOrder ?? 0;
    return sortOrder1 - sortOrder2;
  });
};

export const sortSiteMenuOrdersByMealCategorySortOrder = (
  siteMenuOrders: SiteMenuOrder[]
): SiteMenuOrder[] => {
  return siteMenuOrders.toSorted((smo1, smo2) => {
    const sortOrder1 = smo1?.scheduledMeal?.mealCategory?.sortOrder ?? 0;
    const sortOrder2 = smo2?.scheduledMeal?.mealCategory?.sortOrder ?? 0;
    return sortOrder1 - sortOrder2;
  });
};
export const sortSiteMenuOrdersByRouteThenMealCategorySortOrder = (
  siteMenuOrders: SiteMenuOrder[] | SiteMenuOrderView[]
): SiteMenuOrder[] | SiteMenuOrderView[] => {
  return siteMenuOrders.sort((smo1, smo2) => {
    const route1 = smo1.route?.name;
    const route2 = smo2.route?.name;

    if (route1 == null && route2 != null) return 1;
    if (route1 != null && route2 == null) return -1;

    if (route1 != null && route2 != null) {
      const routeComparison = route1.localeCompare(route2);
      if (routeComparison !== 0) {
        return routeComparison;
      }
    }

    return smo1.scheduledMeal.mealCategory.sortOrder - smo2.scheduledMeal.mealCategory.sortOrder;
  });
};

const utils = {
  updateUrl: (queryParams: Record<any, any>): void => {
    const newUrl = new URL(window.location.origin + window.location.pathname);
    utils.setSearchParams(newUrl, queryParams);
    window.history.pushState({ path: newUrl.toString() }, '', newUrl);
  },
  setSearchParams: (url: URL, obj: any) => {
    if (obj === null) return '';
    Object.keys(obj)
      .filter((key) => obj[key] != null)
      .forEach((key) => {
        Array.isArray(obj[key])
          ? obj[key].forEach((e: any) => {
              url.searchParams.append(key, e);
            })
          : url.searchParams.append(key, obj[key]);
      });
  },
  getContentTypeForFormat: (format: string) => {
    switch (format) {
      case 'json':
        return 'application/json';
      case 'csv':
        return 'text/csv';
      case 'pdf':
        return 'application/pdf';
      case 'xls':
        return 'application/vnd.ms-excel';
    }
  },
  mapWeekDaysToString: (availableDays: string[]): string => {
    const weekDays = ['0', '0', '0', '0', '0', '0', '0'];
    availableDays?.forEach((day) => {
      switch (day) {
        case 'Sunday':
          weekDays[0] = '1';
          break;
        case 'Monday':
          weekDays[1] = '1';
          break;
        case 'Tuesday':
          weekDays[2] = '1';
          break;
        case 'Wednesday':
          weekDays[3] = '1';
          break;
        case 'Thursday':
          weekDays[4] = '1';
          break;
        case 'Friday':
          weekDays[5] = '1';
          break;
        case 'Saturday':
          weekDays[6] = '1';
          break;
      }
    });
    return weekDays.join('');
  },
  addBusinessDays: (orderTime: Dayjs, daysToAdd: number, time: string): Dayjs => {
    let remainingDays = daysToAdd;
    let current = orderTime;
    if (daysToAdd === 0) return orderTime;

    if (daysToAdd >= 5) {
      daysToAdd -= 5;
      const fullWeeks = Math.floor(daysToAdd / 5);
      remainingDays = (daysToAdd % 5) + 5;
      current = current.add(fullWeeks, 'week');
    }

    const currentDay = current.day();

    if (currentDay === 0) {
      current = current.add(1, 'day');
      current = current.startOf('day');
    } else if (currentDay === 6) {
      current = current.add(2, 'day');
      current = current.startOf('day');
    }

    const [hour, minute] = time.split(':').map(Number);
    const dueMinutes = hour * 60 + minute;
    const currentMinutes = current.hour() * 60 + current.minute();
    if (currentMinutes < dueMinutes) {
      remainingDays--;
    }

    while (remainingDays > 0) {
      current = current.add(1, 'd');
      const day = current.day();
      if (day !== 0 && day !== 6) {
        remainingDays--;
      }
    }

    return current;
  },
  mapStringToWeekDays: (availableDays: string): string[] => {
    const listOfDays = [];
    for (let i = 0; i < availableDays.length; i++) {
      availableDays[i] === '1' && listOfDays.push(weekDays[i]);
    }
    return listOfDays;
  },
  cleanEmptyProps,
  dateDisplayFormat: 'MMM DD, YYYY',
  dateTimeDisplayFormat: 'MMM DD, YYYY hh:mm A',
  getAuthorizationHeader,
  stringToColor,
  randomUUID,
  numberFormat: new Intl.NumberFormat('en-US', {
    style: 'decimal',
  }).format,
  currencyFormat: new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format,
  serveTypesLabels,
  serveToolsLabels,
  serviceTypesLabels,
  groupBy,
  groupByToMap,
  printFile,
  openFileInNewWindow,
  downloadFile,
  isValidUUID: (str: string) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(str),
};
export default utils;
